/* eslint-disable no-undef */
import dayjs from "dayjs";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReactDOM from "react-dom/client";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { agent } from "../agent";
import siduat from "../lang/siduat";
import devUpgraded from "../lang/dev-upgraded";
import simdev from "../lang/simdev";
import essecdev from "../lang/essecdev";
import siddev from "../lang/siddev";
import uat from "../lang/uat";
import simuat from "../lang/simuat";
import essecuat from "../lang/essecuat";
import sim from "../lang/sim";
import essec from "../lang/essec";
import mentorl from "../lang/mentorl";
import sid from "../lang/sid";
import PropTypes from "prop-types";
import { useEffect } from "react";
import { resolveTenant, isCustomDomain } from "../customDomains";

const baseToastStyle = {
  fontSize: "16px",
  borderRadius: "8px",
  padding: "12px 20px",
  textAlign: "right",
  fontWeight: "500",
};
// Base Toastify setup
export const showToaster = (message) => {
  toast.success(message, {
    position: "top-right",
    autoClose: 2000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: false,
    style: {
      background: "#0e81e4",
      color: "#fff",
      fontSize: "16px",
      borderRadius: "8px",
      padding: "12px 20px",
      textAlign: "left",
      fontWeight: "500",
      zIndex: 9999,
    },
  });
};

// Error toast
export const showErrorToaster = (message) => {
  toast.error(message, {
    position: "top-right",
    autoClose: 2000,
    hideProgressBar: true,
    pauseOnHover: true,
    draggable: false,
    style: {
      background: "#ef4343",
      color: "#fff",
      fontSize: "16px",
      borderRadius: "8px",
      padding: "12px 20px",
      textAlign: "left",
      fontWeight: "500",
    },
  });
};

export const setToaster = (message) => {
  toast.success(message, {
    position: "top-right",
    autoClose: 2000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: false,
    style: {
      ...baseToastStyle,
      background: "#0e81e4",
      color: "#fff",
    },
  });
};

export const UCWords = (text) => {
  if (!text) return "";
  return text
    .toString()
    .split(" ")
    .map((word) =>
      word.length > 0
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : "",
    )
    .join(" ");
};

export const UCObjective = (text) => {
  if (!text) return "";
  return text.toString().split("||");
};

export const getDateFormat = (date, formate = "YYYY-MM-DD") => {
  return dayjs(date).format(formate);
};

// Warning toast
export const showWarningToaster = (message) => {
  toast.warn(message, {
    position: "top-center",
    autoClose: 2000,
    hideProgressBar: true,
    pauseOnHover: true,
    draggable: false,
    style: {
      background: "#f0ad4e",
      color: "#fff",
      fontSize: "16px",
      borderRadius: "8px",
      padding: "12px 20px",
      textAlign: "left",
      fontWeight: "500",
    },
  });
};

// Toaster with OK confirmation (modal-like)
export const showToasterWithOk = (message, callback) => {
  const ToastContent = ({ closeToast }) => (
    <div
      style={{
        textAlign: "center",
        color: "#000",
        padding: "15px 25px",
      }}
    >
      <p style={{ marginBottom: "15px", fontSize: "16px" }}>{message}</p>
      <button
        onClick={() => {
          closeToast();
          if (callback) callback();
        }}
        style={{
          backgroundColor: "#0e81e4",
          color: "#fff",
          border: "none",
          borderRadius: "6px",
          padding: "8px 25px",
          cursor: "pointer",
          fontWeight: "600",
        }}
      >
        OK
      </button>
    </div>
  );

  toast(<ToastContent />, {
    position: "top-center",
    autoClose: false,
    closeOnClick: false,
    closeButton: false,
    draggable: false,
    hideProgressBar: true,
    style: {
      background: "#fff",
      borderRadius: "12px",
      boxShadow: "0px 0px 15px rgba(0,0,0,0.2)",
      minWidth: "350px",
    },
  });
  ToastContent.propTypes = {
    closeToast: PropTypes.func.isRequired,
  };
};

/* eslint-disable default-param-last */
export function showModalWithOk(message = "Something went wrong.", onClose) {
  // Create a container in the DOM
  const container = document.createElement("div");
  document.body.appendChild(container);

  // Function to close and cleanup
  const handleClose = () => {
    root.unmount();
    container.remove();
    if (onClose) onClose();
  };

  // Render modal
  const root = ReactDOM.createRoot(container);
  root.render(
    <Modal
      show
      centered
      backdrop="static"
      keyboard={false}
      className="dialog_box"
      onHide={handleClose}
    >
      <Modal.Body className="text-center">
        <p className="text-gray-800">{message}</p>
      </Modal.Body>
      <button
        onClick={handleClose}
        className="px-5 m-0 w-100 py-3 rounded-0 fs-5 btn btn_red"
      >
        Ok
      </button>
    </Modal>,
  );
}
/* eslint-enable default-param-last */

// Confirm dialog (Yes / No)
export const checkConform = async (callback, message, cancelCallback) => {
  return new Promise((resolve) => {
    const ConfirmContent = ({ closeToast }) => (
      <div
        style={{
          textAlign: "right",
          color: "#000",
          padding: "15px 25px",
        }}
      >
        <p style={{ marginBottom: "15px", fontSize: "16px" }}>{message}</p>
        <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
          <button
            onClick={() => {
              closeToast();
              if (typeof callback === "function") callback();
              resolve(true);
            }}
            style={{
              backgroundColor: "#0078ff",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              padding: "8px 20px",
              cursor: "pointer",
              fontWeight: "600",
            }}
          >
            Yes
          </button>
          <button
            onClick={() => {
              closeToast();
              if (typeof cancelCallback === "function") cancelCallback();
              resolve(false);
            }}
            style={{
              backgroundColor: "#ccc",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              padding: "8px 20px",
              cursor: "pointer",
              fontWeight: "600",
            }}
          >
            No
          </button>
        </div>
      </div>
    );

    ConfirmContent.propTypes = {
      closeToast: PropTypes.func.isRequired,
    };

    toast(<ConfirmContent />, {
      position: "top-right",
      autoClose: false,
      closeOnClick: false,
      closeButton: false,
      draggable: false,
      hideProgressBar: true,
      style: {
        background: "#fff",
        borderRadius: "12px",
        boxShadow: "0px 0px 15px rgba(0,0,0,0.2)",
        minWidth: "350px",
      },
    });
  });
};

export const renderGlobalModal = ({
  title = "",
  message = "Record has been updated successfully",
  buttonText = "Ok",
}) => {
  // Create a temporary container for modal
  const modalContainer = document.createElement("div");
  document.body.appendChild(modalContainer);

  const root = ReactDOM.createRoot(modalContainer);

  const handleClose = () => {
    root.unmount();
    modalContainer.remove();
  };

  // Render modal dynamically
  root.render(
    <Modal
      show={true}
      onHide={handleClose}
      centered
      backdrop="static"
      keyboard={false}
      className="dialog_box"
    >
      {title && (
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
      )}
      <Modal.Body className="text-center">
        <p className="text-gray-800">{message}</p>
      </Modal.Body>
      <button
        // variant="primary"
        onClick={handleClose}
        className="px-5 m-0 w-100 py-3 rounded-0 fs-5 btn btn_red"
      >
        {buttonText}
      </button>
    </Modal>,
  );
};

export const blobToFile = (theBlob, fileName) =>
  new File([theBlob], fileName, {
    lastModified: Date.now(),
    type: theBlob.type,
  });

// dateUtils.js

/** Returns max allowed DOB (today - 10 years) as YYYY-MM-DD */
export const getMaxDob = () => {
  const today = new Date();
  const maxDate = new Date(
    today.getFullYear() - 10,
    today.getMonth(),
    today.getDate(),
  );

  return maxDate.toLocaleDateString("en-CA"); // avoids timezone issues
};

/** Validates DOB (age must be >= 10 years) */
export const validateDob = (value, isRequired = false) => {
  if (!isRequired && !value) {
    return { valid: true, error: null };
  }

  if (isRequired && !value) {
    return { valid: false, error: "Please select a date." };
  }

  const isValid = /^\d{4}-\d{2}-\d{2}$/.test(value);
  if (!isValid) {
    return { valid: false, error: "Enter valid date format (YYYY-MM-DD)" };
  }
  const selected = new Date(value);
  const limit = new Date();
  limit.setFullYear(limit.getFullYear() - 10);

  if (selected > limit) {
    return { valid: false, error: "You must be at least 10 years old." };
  }

  return { valid: true, error: null };
};

const resolveSubdomain = (fallbackUrl = "https://www.vlookup.ai") => {
  const hostname = globalThis.location.hostname;

  const devHosts = (process.env.REACT_APP_DEV_HOSTS || "")
    .split(",")
    .map((h) => h.trim());

  if (devHosts.includes(hostname)) {
    return "dev-upgraded";
  }

  // Client-owned masked domains (e.g. mentoring.sid.org.sg) map to a tenant
  // explicitly — subdomain parsing below would yield a wrong value for them.
  const customTenant = resolveTenant(hostname);
  if (customTenant) {
    return customTenant;
  }

  const parts = hostname.split(".");

  if (parts.length > 2) {
    return parts.slice(0, -2).join(".");
  }

  alert("Please check the domain and try again");
  globalThis.location.href = fallbackUrl;

  return null;
};

export const getSubdomainPath = () => {
  // Get the full hostname
  return resolveSubdomain();
};

export const getFusionAuthDomain = () => {
  // Get the full hostname
  return resolveSubdomain();
};

export const fetchFusionAuthAPI = createAsyncThunk(
  "fusionAuth/fetchData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agent.get(
        `${process.env.REACT_APP_NODE_SERVER_URL}/login`,
        {
          withCredentials: true, // 🔥 important for cookies
        },
      );

      const url = response?.data?.result?.url;

      if (!url) {
        throw new Error("Authorize URL not received from server");
      }

      // 🔥 Browser redirect (NOT fetch)
      globalThis.location.assign(url);
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  },
);

export const getTranslation = (key) => {
  const domain = getSubdomainPath();

  const domainFiles = {
    siduat: siduat,
    simdev: simdev,
    essecdev: essecdev,
    siddev: siddev,
    uat: uat,
    simuat: simuat,
    essecuat: essecuat,
    sim: sim,
    essec: essec,
    mentorl: mentorl,
    sid: sid,
    "dev-upgraded": devUpgraded,
  };

  // Use domain file if exists, otherwise fallback to dev-upgraded
  const langFile = domainFiles[domain] || domainFiles["dev-upgraded"];

  return langFile[key] || key;
};

const BASE_URL = isCustomDomain(globalThis.location.hostname)
  ? globalThis.location.origin
  : process.env.REACT_APP_NODE_SERVER_URL;

export const handleConfirmLogout = async (actions, setShowModal, isSSOEnabled) => {
  const keysToRemove = [
    "persist:root",
    "userInfo",
    "userToken",
    "userRole",
    "isConfirmed",
    "profileImage",
    "profile_img",
    "mentorEligibilityPopupShown",
  ];

  // Clear the local session first. The navigation below leaves this page, so
  // anything queued after it is not guaranteed to run.
  try {
    actions.userLogout({});
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    sessionStorage.clear();
    setShowModal(false);
  } catch (err) {
    console.error("Logout cleanup failed:", err);
  }

  if (isSSOEnabled) {
    // Log out with a top-level navigation, NOT an axios/XHR call.
    //
    // `${BASE_URL}/logout` clears our own cookies and then redirects the browser
    // on to FusionAuth's /oauth2/logout. FusionAuth's session cookie
    // (fusionauth.sso) is SameSite=Lax, and a Lax cookie is only sent on a
    // top-level navigation — never on a cross-site XHR.
    globalThis.location.href = `${BASE_URL}/logout`;
  }
};

export const useCleanUrl = () => {
  useEffect(() => {
    const url = globalThis.location.href;

    const contains = ["!", "!#", "#", "#!"].some((char) => url.includes(char));

    const newUrl = url
      .replace("#", "")
      .replace("!", "")
      .replace("!#", "")
      .replace("#!", "");

    if (globalThis.location.search.includes("?_escaped_fragment_")) {
      const updatedSearch = globalThis.location.search.replace(
        "?_escaped_fragment_",
        "",
      );

      const updatedURL = `${globalThis.location.origin}${globalThis.location.pathname}${updatedSearch}${globalThis.location.hash}`;

      globalThis.history.replaceState(null, null, updatedURL);
    }

    if (contains) {
      globalThis.history.replaceState(null, null, newUrl);
    }
  }, []);
};

export const normalizeMonthYear = (value) => {
  if (!value) return "";

  // Already yyyy-MM
  if (/^\d{4}-\d{2}$/.test(value)) {
    return value;
  }

  // If yyyy-MM-dd or ISO string
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 7);
  }

  // API returns "Nov 2020"
  const date = new Date(`01 ${value}`);

  if (!isNaN(date.getTime())) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  }

  const fallbackDate = new Date(value);
  if (!isNaN(fallbackDate.getTime())) {
    const year = fallbackDate.getFullYear();
    const month = String(fallbackDate.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  }

  return "";
};

export const scrollToFirstError = () => {
  let hasScrolled = false;

  const tryScroll = () => {
    if (hasScrolled) return true;

    const selector = [
      "input.is-invalid",
      "select.is-invalid",
      "textarea.is-invalid",
      ".react-datepicker__input-container input.is-invalid",
      ".is-invalid",
      "span[role='alert']",
      "div[role='alert']",
      ".invalid-feedback",
      "[aria-invalid='true']",
    ].join(", ");

    const errorElements = document.querySelectorAll(selector);
    if (!errorElements || errorElements.length === 0) return false;

    let target = null;
    for (const el of errorElements) {
      if (el.tagName === "INPUT" && el.type === "hidden") continue;
      if (el.offsetParent !== null || el.getClientRects().length > 0) {
        target = el;
        break;
      }
    }

    if (!target && errorElements.length > 0) {
      for (const el of errorElements) {
        if (el.tagName === "INPUT" && el.type === "hidden") continue;
        target = el;
        break;
      }
    }

    if (!target) return false;

    let focusTarget = target;
    // If target is an error message, find the associated input in the field container
    if (
      target.tagName === "SPAN" ||
      target.tagName === "DIV" ||
      target.tagName === "P" ||
      target.getAttribute("role") === "alert"
    ) {
      const container =
        target.closest(".mb-3, .form-group, .col-md-12, .col-md-6, .react-datepicker-wrapper-custom, [class*='col-']") ||
        target.parentElement;
      const input = container?.querySelector("input:not([type='hidden']), select, textarea, .react-datepicker__input-container input");
      if (input) {
        focusTarget = input;
      }
    }

    // If target is a wrapper (e.g. react-datepicker-wrapper), find the input inside
    if (focusTarget && focusTarget.tagName !== "INPUT" && focusTarget.tagName !== "SELECT" && focusTarget.tagName !== "TEXTAREA") {
      const innerInput = focusTarget.querySelector("input:not([type='hidden']), select, textarea");
      if (innerInput) {
        focusTarget = innerInput;
      }
    }

    focusTarget.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });

    const applyFocus = () => {
      try {
        if (typeof focusTarget.focus === "function") {
          focusTarget.focus({ preventScroll: true });
        }
        if (focusTarget.closest?.(".react-datepicker-wrapper, .react-datepicker__input-container")) {
          focusTarget.click();
        }
        if (
          typeof focusTarget.setSelectionRange === "function" &&
          (focusTarget.type === "text" || focusTarget.type === "search" || focusTarget.type === "url" || focusTarget.type === "tel" || focusTarget.type === "password")
        ) {
          const len = focusTarget.value ? focusTarget.value.length : 0;
          focusTarget.setSelectionRange(len, len);
        }
      } catch (err) {
        // ignore
      }
    };

    applyFocus();
    setTimeout(applyFocus, 100);
    setTimeout(applyFocus, 300);

    hasScrolled = true;
    return true;
  };

  [30, 100, 200, 400, 700].forEach((ms) => {
    setTimeout(tryScroll, ms);
  });
};

